import React, { Component } from "react";
import ReactDOM from "react-dom";
import { MenuItem } from "react-contextmenu";
import uuid from "react-uuid";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import OrderModifyForm from "./orderModifyForm";
import { withTranslation } from "react-i18next";

const formatOrderValue = (value, precision = 0) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

class OrderCell extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModifyModal: false,
      showContextMenu: false,
      contextMenuPosition: { x: 0, y: 0 },
    };
    this.contextMenuRef = React.createRef();
    this.contextMenuId = uuid();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (
      this.contextMenuRef.current &&
      !this.contextMenuRef.current.contains(event.target)
    ) {
      this.setState({ showContextMenu: false });
    }
  };

  handleClick = () => {
    const { disabled, type, data, selectionData, onOrderClicked } = this.props;
    if (disabled || !onOrderClicked) {
      return;
    }

    onOrderClicked(type, selectionData ?? data);
  };

  handleModifyModalClose = () => {
    this.setState({ showModifyModal: false });
  };

  handleModify = () => {
    this.setState({ showModifyModal: true, showContextMenu: false });
  };

  modifyOrder = (orderData) => {
    this.props.modifyOrder(orderData);
  };

  handleWithdraw = () => {
    this.setState({ showContextMenu: false }, () =>
      this.props.withdrawOrder(this.getOrderData())
    );
  };

  handlePick = () => {
    this.setState({ showContextMenu: false }, () =>
      this.props.pickOrder(this.getOrderData())
    );
  };

  handleContextMenu = (e) => {
    if (this.props.disabled) {
      return;
    }

    e.preventDefault();
    this.setState({
      showContextMenu: true,
      contextMenuPosition: { x: e.clientX, y: e.clientY },
    });
  };

  getOrderData = () => {
    const { type, instrumentId, orderId, price, volume } = this.props;
    return {
      instrumentId,
      orderId,
      orderDirection: type === "Bid" ? "Buy" : "Sell",
      price,
      volume,
    };
  };

  renderContextMenu = () => {
    const {
      isUserOrder,
      allowPick,
      isOrganizationOrder,
      tradeAllowed,
      tradeOnBehalf,
      orderId,
    } = this.props;
    const { contextMenuPosition } = this.state;

    const hasOrder = !!orderId;
    const canModifyOrWithdraw = hasOrder && (isUserOrder || tradeOnBehalf);
    const canPick = hasOrder && tradeAllowed && allowPick && !isOrganizationOrder;
    const shouldShow = canModifyOrWithdraw || canPick;

    if (!shouldShow) return null;

    return ReactDOM.createPortal(
      <div
        ref={this.contextMenuRef}
        style={{
          position: "fixed",
          top: contextMenuPosition.y,
          left: contextMenuPosition.x,
          background: "ghostwhite",
          fontSize: "14px",
          color: "black",
          padding: "5px 20px",
          borderRadius: "10px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          zIndex: 9999,
        }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {canModifyOrWithdraw && (
          <MenuItem onClick={this.handleModify}>
            <div style={{ padding: "5px 0", cursor: "pointer" }}>Modify</div>
          </MenuItem>
        )}
        {canModifyOrWithdraw && (
          <MenuItem onClick={this.handleWithdraw}>
            <div style={{ padding: "5px 0", cursor: "pointer" }}>Withdraw</div>
          </MenuItem>
        )}
        {canPick && (
          <MenuItem onClick={this.handlePick}>
            <div style={{ padding: "5px 0", cursor: "pointer" }}>Pick</div>
          </MenuItem>
        )}
      </div>,
      document.body
    );
  };

  render() {
    const {
      t,
      value,
      type,
      isDepth,
      pricePrecision,
      volumePrecision,
      uomName,
      currencyName,
      currencySymbol,
      align,
      data,
      disabled,
      isOrganizationOrder,
      isUserOrder,
      superscript,
      displayMode,
      contentType = "price",
      showOwnershipIcon = true,
    } = this.props;

    const { marketName, priceBasisName, termName, qualityAttributeValues } =
      data;

    const { showModifyModal } = this.state;

    const valuePrecision =
      contentType === "volume" ? volumePrecision ?? 0 : pricePrecision ?? 0;
    const cellValue = formatOrderValue(value, valuePrecision);
    const backgroundColor =
      type === "Bid" ? "0,0,255" : type === "Offer" ? "255,0,0" : "none";
    const backgroundOpacity = isDepth ? ".15" : ".35";
    const background = `rgba(${backgroundColor},${backgroundOpacity})`;
    const isBid = type === "Bid";
    const isOffer = type === "Offer";
    const isNeutral = !isBid && !isOffer;
    const isVolume = contentType === "volume";
    const ownershipIcon = showOwnershipIcon && isVolume
      ? isUserOrder
        ? faUser
        : isOrganizationOrder
          ? faUserGroup
          : null
      : null;

    const orderData = this.getOrderData();

    if (displayMode === "marketGridBadge") {
      return (
        <div className="market-grid-order-wrapper" onContextMenu={this.handleContextMenu}>
          <div
            className={`${disabled ? "" : "clickable"} market-grid-order-cell ${
              isBid ? "is-bid" : "is-offer"
            } ${isDepth ? "is-depth" : ""} ${
              disabled ? "is-disabled" : ""
            }`}
            onClick={this.handleClick}
            style={{
              justifyContent: !align
                ? "center"
                : isBid
                  ? "flex-end"
                  : isNeutral
                    ? "center"
                  : "flex-start",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            {isBid && superscript && (
              <sup className="market-grid-order-superscript">{superscript}</sup>
            )}
            {isVolume ? (
              <span
                className={`market-grid-order-volume ${
                  isDepth ? "is-depth" : ""
                }`}
              >
                {isBid && ownershipIcon && (
                  <FontAwesomeIcon
                    icon={ownershipIcon}
                    className="market-grid-order-ownership-icon"
                  />
                )}
                <span className="market-grid-order-volume-value">{cellValue}</span>
                {isOffer && ownershipIcon && (
                  <FontAwesomeIcon
                    icon={ownershipIcon}
                    className="market-grid-order-ownership-icon"
                  />
                )}
              </span>
            ) : (
              <span
                className={`market-grid-order-badge ${
                  isBid ? "bid" : isOffer ? "offer" : "neutral"
                } ${isDepth ? "is-depth" : ""} is-price`}
              >
                <span className="market-grid-order-badge-value">{cellValue}</span>
              </span>
            )}
            {isOffer && superscript && (
              <sup className="market-grid-order-superscript">{superscript}</sup>
            )}
          </div>

          {this.state.showContextMenu && this.renderContextMenu()}

          <Modal
            show={showModifyModal}
            onHide={this.handleModifyModalClose}
            size="md"
            backdrop={false}
            animation={false}
            keyboard={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>{t("ModifyOrder")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <OrderModifyForm
                orderData={orderData}
                modifyOrder={this.modifyOrder}
                handleModalClose={this.handleModifyModalClose}
                pricePrecision={pricePrecision}
                volumePrecision={volumePrecision}
                currencyName={currencyName}
                currencySymbol={currencySymbol}
                uomName={uomName}
                marketName={marketName}
                priceBasisName={priceBasisName}
                termName={termName}
                qualityAttributeValues={qualityAttributeValues}
              />
            </Modal.Body>
          </Modal>
        </div>
      );
    }

    return (
      <div onContextMenu={this.handleContextMenu}>
        <div
          className={`${disabled ? "" : "clickable"} ${
            isOrganizationOrder ? "theme-highlighted-text" : ""
          }`}
          onClick={this.handleClick}
          style={{
            background,
            color: isOrganizationOrder ? undefined : "white",
            paddingLeft: "10px",
            paddingRight: "10px",
            fontWeight: isDepth ? "normal" : "bold",
            textAlign: !align ? "center" : type === "Bid" ? "right" : "left",
            width: "100%",
            height: "100%",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.45 : 1,
          }}
        >
          {type === "Bid" && <sup style={{ fontSize: 9 }}>{superscript}</sup>}
          <span>{cellValue}</span>
          {type === "Offer" && (
            <sup style={{ fontSize: 9 }}>{superscript}</sup>
          )}
        </div>

        {this.state.showContextMenu && this.renderContextMenu()}

        <Modal
          show={showModifyModal}
          onHide={this.handleModifyModalClose}
          size="md"
          backdrop={false}
          animation={false}
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("ModifyOrder")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <OrderModifyForm
              orderData={orderData}
              modifyOrder={this.modifyOrder}
              handleModalClose={this.handleModifyModalClose}
              pricePrecision={pricePrecision}
              volumePrecision={volumePrecision}
              currencyName={currencyName}
              currencySymbol={currencySymbol}
              uomName={uomName}
              marketName={marketName}
              priceBasisName={priceBasisName}
              termName={termName}
              qualityAttributeValues={qualityAttributeValues}
            />
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

export default withTranslation(["marketdata"])(OrderCell);
